import React, { useState, useMemo, useEffect } from "react";
import Papa from "papaparse";
import { Form, Input, Space, Button, message, Upload, Alert, Radio } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import UploadCsv from "../../../../components/UploadCsv";
import useLdl from "../../../../hooks/useLdl";
import useAuth from "../../../../hooks/useAuth";
import CopyToClipBoard from "../../../../components/CopyToClipBoard";
import type { UploadFile } from "antd/es/upload/interface";
type Props = {};

const GlobalHygiene: React.FC<Props> = () => {
  const ldl = useLdl();
  const { user }: any = useAuth();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [defaultFileList, setDefaultFileList] = useState<UploadFile<any>[]>([]);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorList, setErrorList] = useState("");
  const disabled = useMemo(
      () =>
        !ldl.globalHygieneSchema.isValidSync(ldl.globalHygiene) ||
        !ldl.globalHygiene.file,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [ldl.globalHygiene]
    );

  useEffect(() => {
    if (errorList) message.error(errorList);
  }, [errorList]);

  const validateColumns = (results: any): boolean =>
    results.data.some((line: any, index: number) => {
      const col1NotANumber = line[0] && !Number(line[0]);
      // if value is not a number
      if (col1NotANumber)
        setErrorList(`Line ${index + 1}: [${line[0]}] is not a valid number`);

      const col2NotANumber = line[1] && !Number(line[1]);
      // if value is not a number
      if (col2NotANumber)
        setErrorList(`Line ${index + 1}: [${line[1]}] is not a valid number`);

      return col1NotANumber || col2NotANumber;
    });

  const beforeUpload = (file: any) => {
    setUploadDisabled(true);
    setErrorList("");
    if (file.type !== "text/csv") {
      message.error(`${file.name} is not a csv file`);
      setUploadDisabled(false);
      return Upload.LIST_IGNORE;
    }

    Papa.parse(file, {
      skipEmptyLines: true,
      delimiter: ",",
      complete: function (results) {
        console.log({ results });
        // validates file is not empty
        if (!results.data.length) {
          message.error("File must not be empty");
          setDefaultFileList([]);
        }

        // validates file hs 3 columns
        if (results.data.some((line: any) => line.length !== 3)) {
          message.error("File should have three columns (party id, email id, source)");
          setDefaultFileList([]);
        }

        if (validateColumns(results)) {
          setUploadDisabled(false);
          setDefaultFileList([]);
          return;
        }

        ldl.actions.setGlobalHygiene({
          ...ldl.globalHygiene,
          filename: file.name.split(".csv")[0].split(" ").join("_"),
          file: file,
          csv: Papa.unparse(results.data, { newline: "\n" }).replace(/"/g, ""),
        });

        setUploadDisabled(false);
      },
    });

    return false;
  };

  const onChange = (info: any) => {
    setDefaultFileList(info.fileList);
    if (!info.fileList.length) {
      ldl.actions.setGlobalHygiene({
        ...ldl.globalHygiene,
        filename: "",
        file: null,
        csv: "",
      });
    }

    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  return (
    <>
      <div className="site-page-header-ghost-wrapper">
        <PageHeader
          ghost={false}
          title="LDL"
          subTitle="Hygiene Update"
        />
        {successMessage && (
          <Space
            size={"large"}
            direction="horizontal"
            style={{ marginBottom: 20 }}
          >
            <Alert
              message={`The job ${successMessage} has been created.`}
              type="info"
            />
            <CopyToClipBoard text={successMessage} />
          </Space>
        )}
      </div>
      <Space direction="vertical" size={"large"}>
        <Form name="basic" layout="vertical" form={form}>
          <Space direction="horizontal" size={"large"}>
            <Form.Item
              label="User Name"
              name="username"
              style={{ width: 250 }}
              initialValue={user?.usernameOG}
            >
              <Input disabled readOnly />
            </Form.Item>

            <Form.Item
              label="Request Date"
              name="requestDate"
              style={{ width: 250 }}
              initialValue={new Date().toISOString()}
            >
              <Input disabled readOnly />
            </Form.Item>
          </Space>

          <Form.Item label="File To Load">
            <UploadCsv
              beforeUpload={beforeUpload}
              onChange={onChange}
              defaultFileList={defaultFileList}
              setDefaultFileList={setDefaultFileList}
              disabled={uploadDisabled}
              setDisabled={setUploadDisabled}
            />
          </Form.Item>

          <Space direction="horizontal" size={"large"}>
            <Form.Item label="Target List" name="targetList" style={{ width: 250, textAlign: "left" }}>
              <Radio.Group
                options={[
                  { value: "GWL", label: "Whitelist"},
                  { value: "GBL", label: "Blacklist"}
                ]}
                onChange={(e) =>
                  ldl.actions.setGlobalHygiene({
                    ...ldl.globalHygiene,
                    targetList: e.target.value,
                  })
                }
                value={ldl.globalHygiene.targetList}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>

            <Form.Item label="Source" name="sourceBU" style={{ width: 250, textAlign: "left" }}>
              <Radio.Group
                options={[
                  { value: "EOPS", label: "EOPS"},
                  { value: "Labs", label: "Labs"}
                ]}
                onChange={(e) =>
                  ldl.actions.setGlobalHygiene({
                    ...ldl.globalHygiene,
                    sourceBU: e.target.value,
                  })
                }
                value={ldl.globalHygiene.sourceBU}
                optionType="button"
                buttonStyle="solid"
              />
            </Form.Item>
          </Space>

          <Form.Item>
            <Button
              className="button-primary"
              type="primary"
              onClick={() =>
                ldl.actions.uploadGlobalHygiene({
                  setIsLoading,
                  form,
                  setDefaultFileList,
                  setDisabled: setUploadDisabled,
                  setSuccessMessage,
                })
              }
              disabled={disabled}
              loading={isLoading}
              style={{ width: 250, marginTop: 30 }}
            >
              EXECUTE
            </Button>
          </Form.Item>
        </Form>
      </Space>
    </>
  );
};

export default GlobalHygiene;
